<template>
  <div class="container-fluid">
    <NavHeader
      pretitle="aqfer Marketing Data Platform"
      title="Audience Manager"
      :navItems="navItems"
    >
      <template #header-button-content>
        <div v-if="isListView && showCreateButton">
          <button
            class="btn btn-primary"
            @click="onCreateAudience"
            :disabled="userRoleRank >= 4"
          >
            <i class="fa fa-plus me-2"></i>
            Create Audience
          </button>
        </div>
        <div v-else-if="isListView" class="d-flex gap-2">
          <button
            class="btn btn-secondary"
            @click="handleAggregateClick('Union')"
            :disabled="selectedAudiences.length < 2"
          >
            <i class="fa fa-plus me-2"></i>
            Union
          </button>
          <button
            class="btn btn-secondary"
            @click="handleAggregateClick('Intersect')"
            :disabled="selectedAudiences.length < 2"
          >
            <i class="fa fa-circle-nodes me-2"></i>
            Intersect
          </button>
          <button class="btn btn-outline-secondary" @click="clearSelection">
            <i class="fa fa-xmark me-2"></i>
            Cancel
          </button>
        </div>
      </template>
    </NavHeader>

    <div>
      <!-- Dashboard Tab -->
      <div v-if="isDashboardView">
        <AudienceDashboard />
      </div>

      <!-- Audiences List Tab -->
      <div v-if="isListView">
        <!-- Audiences Table -->
        <div class="card">
          <div class="card-body p-0">
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading audiences...</span>
              </div>
              <p class="text-muted mt-3">Loading audiences...</p>
            </div>

            <div v-else-if="audiences.length > 0" class="table-responsive">
              <table class="table table-hover align-middle mb-0">
                <thead class="table-light">
                  <tr>
                    <th style="width: 50px;" class="text-center">
                      <input
                        ref="selectAllCheckbox"
                        class="form-check-input"
                        type="checkbox"
                        :checked="isAllSelected"
                        @change="toggleSelectAll"
                        aria-label="Select all audiences"
                      >
                    </th>
                    <th class="sortable" @click="toggleSort('audience_name')">
                      Audience Name
                      <i class="fa ms-1" :class="getSortIcon('audience_name')"></i>
                    </th>
                    <th class="d-none d-md-table-cell sortable" @click="toggleSort('audsum_dataset')">
                      Dataset
                      <i class="fa ms-1" :class="getSortIcon('audsum_dataset')"></i>
                    </th>
                    <th class="d-none d-lg-table-cell sortable" @click="toggleSort('created_by')">
                      Created By
                      <i class="fa ms-1" :class="getSortIcon('created_by')"></i>
                    </th>
                    <th class="d-none d-xl-table-cell sortable" @click="toggleSort('created_date')">
                      Created
                      <i class="fa ms-1" :class="getSortIcon('created_date')"></i>
                    </th>
                    <th class="d-none d-xl-table-cell sortable" @click="toggleSort('updated_date')">
                      Updated
                      <i class="fa ms-1" :class="getSortIcon('updated_date')"></i>
                    </th>
                    <th style="width: 180px;" class="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="audience in audiences" :key="audience.audience_name">
                    <td class="text-center">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        :checked="checkboxSelectedMap[audience.audience_name]"
                        @change="handleAudienceSelection($event, audience)"
                        :disabled="isCheckboxDisabled(audience)"
                        :aria-label="`Select ${audience.audience_name}`"
                      >
                    </td>
                    <td>
                      <div class="d-flex align-items-center">
                        <i class="fa fa-users text-primary me-2"></i>
                        <a href="#" @click.prevent="viewAudienceDetail(audience.audience_name)" class="text-decoration-none">
                          <strong>{{ audience.audience_name }}</strong>
                        </a>
                      </div>
                      <div class="d-md-none text-muted small mt-1">
                        {{ audience.audsum_dataset }}
                      </div>
                    </td>
                    <td class="d-none d-md-table-cell">
                      <span class="badge bg-light text-dark border">{{ audience.audsum_dataset }}</span>
                    </td>
                    <td class="d-none d-lg-table-cell text-muted">{{ audience.created_by }}</td>
                    <td class="d-none d-xl-table-cell text-muted small">{{ formatDate(audience.created_date) }}</td>
                    <td class="d-none d-xl-table-cell text-muted small">{{ formatDate(audience.updated_date) }}</td>
                    <td class="text-center">
                      <div class="btn-group" role="group">
                        <button
                          class="btn btn-sm btn-outline-primary"
                          @click="viewAudienceDetail(audience.audience_name)"
                          title="View Details"
                        >
                          <i class="fa fa-eye"></i>
                        </button>
                        <div class="btn-group" role="group">
                          <button
                            class="btn btn-sm btn-outline-secondary dropdown-toggle"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            title="More actions"
                          >
                            <i class="fa fa-ellipsis"></i>
                          </button>
                          <ul class="dropdown-menu dropdown-menu-end">
                            <li v-if="!isAggregateAudience(audience)">
                              <a class="dropdown-item" href="#" @click.prevent="handleSubtractAudience(audience)">
                                <i class="fa fa-circle-minus me-2"></i>
                                Subtract Audience
                              </a>
                            </li>
                            <li v-if="!isAggregateAudience(audience)"><hr class="dropdown-divider"></li>
                            <li>
                              <a
                                class="dropdown-item text-danger"
                                href="#"
                                @click.prevent="confirmDelete(audience)"
                                :class="{ disabled: userRoleRank >= 4 }"
                              >
                                <i class="fa fa-trash me-2"></i>
                                Delete
                              </a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Empty State -->
            <div v-else class="text-center py-5">
              <i class="fa fa-users fa-3x text-muted mb-3"></i>
              <h4 class="text-muted">No Audiences Yet</h4>
              <p class="text-muted">Create your first audience to get started</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavHeader from '../../components/NavHeader.vue'
import AudienceDashboard from '../../components/audience/AudienceDashboard.vue'
import { mapActions } from 'vuex'
import { DateTimeFormat } from '../../utils/commonFunction'

export default {
  name: 'Audiences',
  components: { NavHeader, AudienceDashboard },
  data() {
    return {
      loading: true,
      selectedAudiences: [],
      selectedDataset: null,
      checkboxSelectedMap: {},
      sortColumn: 'audience_name',
      sortDirection: 'asc',
    }
  },
  computed: {
    navItems() {
      return [
        {
          name: 'Dashboard',
          link: { name: 'Audience Dashboard' }
        },
        {
          name: 'Audiences',
          link: { name: 'Audience List' }
        }
      ]
    },
    isDashboardView() {
      return this.$route.name === 'Audience Dashboard' || this.$route.name === 'Audiences'
    },
    isListView() {
      return this.$route.name === 'Audience List'
    },
    audiences() {
      const list = this.$store.state.audience.audiences || []
      return this.sortAudiences(list)
    },
    userRoleRank() {
      return this.$store.getters.userRoles.userRoleRank
    },
    showCreateButton() {
      return this.selectedAudiences.length === 0
    },
    selectableAudiences() {
      return this.audiences.filter(audience => !this.isCheckboxDisabled(audience))
    },
    isAllSelected() {
      const selectable = this.selectableAudiences
      return selectable.length > 0 && this.selectedAudiences.length === selectable.length
    },
    isSomeSelected() {
      return this.selectedAudiences.length > 0 && this.selectedAudiences.length < this.selectableAudiences.length
    },
  },
  methods: {
    ...mapActions({
      showModal: 'showModal',
      fetchAudiences: 'audience/fetchAudiences',
      deleteAudience: 'audience/deleteAudience',
      addToast: 'addToast',
    }),
    async loadAudiences() {
      this.loading = true
      try {
        await this.fetchAudiences()
        // Initialize checkbox map
        this.audiences.forEach(audience => {
          this.checkboxSelectedMap[audience.audience_name] = false
        })
      } catch (error) {
        this.addToast({ type: 'danger', message: 'Failed to load audiences' })
      } finally {
        this.loading = false
      }
    },
    viewAudienceDetail(audienceName) {
      this.$router.push(`/${this.$store.state.currentClient.cid}/amdp/audiences/${audienceName}`)
    },
    onCreateAudience() {
      this.showModal({ component: 'AddAudience' })
    },
    findCriterionType(audience) {
      if (audience.parent_audiences && audience.parent_audiences !== '') {
        return 'Aggregate'
      }
      try {
        const query = JSON.parse(audience.audience_criterion_json || '{}')
        if (!query || !query.criterion) {
          return 'Simple'
        }
        if (query.criterion.operator === 'not') {
          return 'Complex'
        }
        if (query.criterion.operands && query.criterion.operands.some(op => 'operands' in op)) {
          return 'Complex'
        }
        return 'Simple'
      } catch {
        return 'Simple'
      }
    },
    isAggregateAudience(audience) {
      return audience.parent_audiences && audience.parent_audiences !== ''
    },
    isCheckboxDisabled(audience) {
      const audienceType = this.findCriterionType(audience)
      if (audienceType !== 'Simple') {
        return true
      }
      return this.selectedDataset !== null && this.selectedDataset !== audience.audsum_dataset
    },
    toggleSelectAll(event) {
      const isChecked = event.target.checked
      this.audiences.forEach(audience => {
        if (!this.isCheckboxDisabled(audience)) {
          this.checkboxSelectedMap[audience.audience_name] = isChecked
          if (isChecked && !this.selectedAudiences.find(a => a.id === audience.audience_name)) {
            this.selectedAudiences.push(audience)
            if (this.selectedAudiences.length === 1) {
              this.selectedDataset = audience.audsum_dataset
            }
          }
        }
      })
      if (!isChecked) {
        this.selectedAudiences = []
        this.selectedDataset = null
      }
    },
    handleAudienceSelection(event, audience) {
      const isChecked = event.target.checked

      if (isChecked) {
        this.checkboxSelectedMap[audience.audience_name] = true
        if (!this.selectedAudiences.find(a => a.id === audience.audience_name)) {
          this.selectedAudiences.push(audience)
        }
        if (this.selectedAudiences.length === 1) {
          this.selectedDataset = audience.audsum_dataset
        }
      } else {
        this.checkboxSelectedMap[audience.audience_name] = false
        this.selectedAudiences = this.selectedAudiences.filter(a => a.id !== audience.audience_name)
        if (this.selectedAudiences.length === 0) {
          this.selectedDataset = null
        }
      }
      this.$forceUpdate()
    },
    clearSelection() {
      this.selectedDataset = null
      this.selectedAudiences = []
      for (const key in this.checkboxSelectedMap) {
        this.checkboxSelectedMap[key] = false
      }
    },
    handleAggregateClick(type) {
      this.showModal({
        component: 'ListSelectedAudience',
        data: {
          type,
          selectedAudiences: this.selectedAudiences,
          checked: true,
        }
      })
    },
    handleSubtractAudience(audience) {
      // Get all Simple audiences in the same dataset (excluding the target audience)
      const audienceToConsider = this.getSubtractableAudiences(audience)
      if (audienceToConsider.length === 0) {
        this.addToast({
          type: 'warning',
          message: 'No other audiences available to subtract in the same dataset'
        })
        return
      }
      this.showModal({
        component: 'ListSelectedAudience',
        data: {
          type: 'Subtract',
          selectedAudiences: audienceToConsider,
          audience,
          checked: false,
        }
      })
    },
    getSubtractableAudiences(targetAudience) {
      // Return all Simple audiences in the same dataset, excluding the target
      return this.audiences.filter(audience => {
        return (
          audience.audience_name !== targetAudience.audience_name &&
          audience.audsum_dataset === targetAudience.audsum_dataset &&
          this.findCriterionType(audience) === 'Simple'
        )
      })
    },
    confirmDelete(audience) {
      if (this.userRoleRank >= 4) return

      const self = this
      this.showModal({
        component: 'Confirm',
        data: {
          title: 'Delete Audience',
          message: `Are you sure you want to delete "${audience.audience_name}"? This action cannot be undone.`,
          async confirm() {
            try {
              await self.deleteAudience(audience.audience_name)
              self.addToast({ type: 'success', message: 'Audience deleted successfully' })
              self.loadAudiences()
            } catch (error) {
              self.addToast({
                type: 'danger',
                message: error.response?.data?.message || error.message || 'Failed to delete audience'
              })
            }
          }
        }
      })
    },
    formatDate(dateString) {
      return DateTimeFormat(dateString)
    },
    sortAudiences(list) {
      if (!this.sortColumn) return list
      return [...list].sort((a, b) => {
        let valA = a[this.sortColumn]
        let valB = b[this.sortColumn]

        // Handle null/undefined
        if (valA == null) valA = ''
        if (valB == null) valB = ''

        // Handle dates
        if (this.sortColumn.includes('date')) {
          valA = new Date(valA).getTime() || 0
          valB = new Date(valB).getTime() || 0
        } else if (typeof valA === 'string') {
          valA = valA.toLowerCase()
          valB = valB.toLowerCase()
        }

        let result = 0
        if (valA < valB) result = -1
        if (valA > valB) result = 1

        return this.sortDirection === 'asc' ? result : -result
      })
    },
    toggleSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = column
        this.sortDirection = 'asc'
      }
    },
    getSortIcon(column) {
      if (this.sortColumn !== column) return 'fa-sort'
      return this.sortDirection === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
    },
  },
  mounted() {
    this.loadAudiences()
  },
  updated() {
    // Set indeterminate state on the select-all checkbox
    if (this.$refs.selectAllCheckbox) {
      this.$refs.selectAllCheckbox.indeterminate = this.isSomeSelected
    }
  },
}
</script>

<style scoped>
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}

.table th.sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.table th.sortable:hover {
  background-color: #e9ecef;
}

.table th.sortable i {
  opacity: 0.4;
  font-size: 0.7rem;
}

.table th.sortable i.fa-sort-up,
.table th.sortable i.fa-sort-down {
  opacity: 1;
  color: var(--bs-primary);
}

.dropdown-menu {
  z-index: 1070 !important;
}
</style>
